
import { useState } from 'react';
import { useUnifiedUser } from '@/hooks/useUnifiedUser';
import { useIsMobile } from '@/hooks/use-mobile';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import GameCodeEntryModal from '@/components/scoring/GameCodeEntryModal';
import LiveGameScoring from '@/components/scoring/LiveGameScoring';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Trophy, Target, Clock, CheckCircle } from 'lucide-react';
import PlayerStatsDisplay from '@/components/stats/PlayerStatsDisplay';
import StatSubmissionModal from '@/components/stats/StatSubmissionModal';
import StatsConfirmationPanel from '@/components/stats/StatsConfirmationPanel';

const PlayerStats = () => {
  const { user } = useUnifiedUser();
  const isMobile = useIsMobile();
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [selectedGame, setSelectedGame] = useState<any>(null);
  const [showLiveScoring, setShowLiveScoring] = useState(false);

  if (!user) {
    return (
      <>
        {isMobile && <MobileHeader />}
        {!isMobile && (
          <>
            <Header />
            <div className="min-h-screen bg-background pt-20">
              <main className="container mx-auto px-4 py-8">
                <div className="max-w-4xl mx-auto">
                  <Card className="bg-card border-border">
                    <CardContent className="p-8 text-center">
                      <p className="text-muted-foreground">Please log in to view your stats.</p>
                    </CardContent>
                  </Card>
                </div>
              </main>
            </div>
            <Footer />
          </>
        )}
        {isMobile && (
          <div className="min-h-screen bg-background pb-20">
            <main className="container mx-auto px-4 py-8">
              <div className="max-w-4xl mx-auto">
                <Card className="bg-card border-border">
                  <CardContent className="p-8 text-center">
                    <p className="text-muted-foreground">Please log in to view your stats.</p>
                  </CardContent>
                </Card>
              </div>
            </main>
          </div>
        )}
        {isMobile && <MobileBottomNav />}
      </>
    );
  }

  const isCoachOrSupport = user.role === 'coach' || user.role === 'support';

  const handleGameSelected = (game: any) => {
    setSelectedGame(game);
    setShowLiveScoring(true);
  };

  const handleBackToStats = () => {
    setShowLiveScoring(false);
    setSelectedGame(null);
  };

  // Show Live Game Scoring if a game is selected
  if (showLiveScoring && selectedGame) {
    return (
      <LiveGameScoring 
        game={selectedGame} 
        onBack={handleBackToStats}
      />
    );
  }

  return (
    <>
      {isMobile && <MobileHeader />}
      {!isMobile && (
        <>
          <Header />
          <div className="min-h-screen bg-background pt-20">
            <main className="container mx-auto px-4 py-8">
              <div className="max-w-6xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-3xl font-bold text-foreground mb-2">Player Statistics</h1>
                    <p className="text-muted-foreground">
                      Track and manage your performance across all sports
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setShowSubmissionModal(true)}
                      variant="outline"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Submit Stats
                    </Button>
                    <GameCodeEntryModal onGameSelected={handleGameSelected}>
                      <Button>
                        Live Scoring
                      </Button>
                    </GameCodeEntryModal>
                  </div>
                </div>

                {/* Quick Stats Overview */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="bg-card border-border">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">
                        <Trophy className="h-4 w-4 inline mr-2" />
                        Total Points
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-foreground">--</div>
                      <p className="text-xs text-muted-foreground">Across all sports</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-card border-border">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">
                        <Target className="h-4 w-4 inline mr-2" />
                        Games Played
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-foreground">--</div>
                      <p className="text-xs text-muted-foreground">This season</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-card border-border">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">
                        <Clock className="h-4 w-4 inline mr-2" />
                        Pending Stats
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-yellow-500">--</div>
                      <p className="text-xs text-muted-foreground">Awaiting confirmation</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-card border-border">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">
                        <CheckCircle className="h-4 w-4 inline mr-2" />
                        Confirmed Stats
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-sports-green">--</div>
                      <p className="text-xs text-muted-foreground">Verified by coaches</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Main Stats Display */}
                <PlayerStatsDisplay playerId={user.id} showSubmitButton={false} />

                {/* Stats Confirmation Panel for Coaches/Support */}
                {isCoachOrSupport && (
                  <div className="mt-8">
                    <StatsConfirmationPanel />
                  </div>
                )}

                {/* Stats Submission Modal */}
                <StatSubmissionModal
                  isOpen={showSubmissionModal}
                  onClose={() => setShowSubmissionModal(false)}
                  playerId={user.id}
                />
              </div>
            </main>
          </div>
          <Footer />
        </>
      )}
      
      {isMobile && (
        <div className="min-h-screen bg-background pb-20">
          <main className="container mx-auto px-4 py-8">
            <div className="max-w-6xl mx-auto space-y-6">
              {/* Header */}
              <div className="flex flex-col space-y-4">
                <div>
                  <h1 className="text-2xl font-bold text-foreground mb-2">Player Statistics</h1>
                  <p className="text-muted-foreground text-sm">
                    Track and manage your performance across all sports
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setShowSubmissionModal(true)}
                    variant="outline"
                    size="sm"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Submit Stats
                  </Button>
                  <GameCodeEntryModal onGameSelected={handleGameSelected}>
                    <Button size="sm">
                      Live Scoring
                    </Button>
                  </GameCodeEntryModal>
                </div>
              </div>

              {/* Quick Stats Overview */}
              <div className="grid grid-cols-2 gap-4">
                <Card className="bg-card border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs font-medium text-muted-foreground">
                      <Trophy className="h-3 w-3 inline mr-1" />
                      Total Points
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-lg font-bold text-foreground">--</div>
                    <p className="text-xs text-muted-foreground">All sports</p>
                  </CardContent>
                </Card>

                <Card className="bg-card border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs font-medium text-muted-foreground">
                      <Target className="h-3 w-3 inline mr-1" />
                      Games Played
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-lg font-bold text-foreground">--</div>
                    <p className="text-xs text-muted-foreground">This season</p>
                  </CardContent>
                </Card>

                <Card className="bg-card border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs font-medium text-muted-foreground">
                      <Clock className="h-3 w-3 inline mr-1" />
                      Pending Stats
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-lg font-bold text-yellow-500">--</div>
                    <p className="text-xs text-muted-foreground">Awaiting confirmation</p>
                  </CardContent>
                </Card>

                <Card className="bg-card border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs font-medium text-muted-foreground">
                      <CheckCircle className="h-3 w-3 inline mr-1" />
                      Confirmed Stats
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-lg font-bold text-sports-green">--</div>
                    <p className="text-xs text-muted-foreground">Verified by coaches</p>
                  </CardContent>
                </Card>
              </div>

              {/* Main Stats Display */}
              <PlayerStatsDisplay playerId={user.id} showSubmitButton={false} />

              {/* Stats Confirmation Panel for Coaches/Support */}
              {isCoachOrSupport && (
                <div className="mt-8">
                  <StatsConfirmationPanel />
                </div>
              )}

              {/* Stats Submission Modal */}
              <StatSubmissionModal
                isOpen={showSubmissionModal}
                onClose={() => setShowSubmissionModal(false)}
                playerId={user.id}
              />
            </div>
          </main>
        </div>
      )}
      {isMobile && <MobileBottomNav />}
    </>
  );
};

export default PlayerStats;
